
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Header } from "@/components/Header";
import { DragDropProvider } from "@/components/dashboard/DragDropProvider";
import { useAuthContext } from '@/contexts/BetterAuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { session, user, loading } = useAuthContext();
  const navigate = useNavigate();
  // Track whether we've ever seen a valid session in this mount cycle.
  // This prevents redirect-to-login during transient auth re-checks.
  const hasEverHadSession = useRef(false);

  if (session || user) {
    hasEverHadSession.current = true;
  }

  const isDemoMode = sessionStorage.getItem('demo_mode') === 'true';

  useEffect(() => {
    // Skip redirect if still loading or if demo mode is active
    if (loading || isDemoMode) {
      return;
    }

    // Only redirect if we have definitively confirmed there is no session
    // AND we never had a session in this mount cycle (prevents flicker on
    // transient re-auth).
    if (!session && !user && !hasEverHadSession.current) {
      navigate("/login");
    }
  }, [session, user, loading, navigate, isDemoMode]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-blue" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render layout if not authenticated (unless in demo mode or we had a session)
  if (!session && !user && !isDemoMode && !hasEverHadSession.current) {
    return null;
  }

  return (
    <SidebarProvider>
      <DragDropProvider>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 p-6 pb-8 bg-background">
              {children}
            </main>
          </div>
        </div>
      </DragDropProvider>
    </SidebarProvider>
  );
}
